import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppTheme } from '../../settings/appTheme';
import { PagesRoutes, Routes } from '../../store/routes';

const POPUP_QUERY_KEY = 'land';

const slides = [
  {
    id: 'stage-1',
    image: 'content/img/landing/stage_1.png',
  },
  {
    id: 'stage-2',
    image: 'content/img/landing/stage_2.png',
  },
  {
    id: 'stage-3',
    image: 'content/img/landing/stage_3.png',
    showCta: true,
  },
];

export const LandPopup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hasPopupParam = searchParams.has(POPUP_QUERY_KEY);
  const [activeSlide, setActiveSlide] = useState(0);

  const isMainPage =
    location.pathname === PagesRoutes.Main ||
    location.pathname === Routes.Main ||
    location.pathname === Routes.Main.replace(/\/$/, '');

  if (!isMainPage || !hasPopupParam) {
    return null;
  }

  const handleClose = () => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete(POPUP_QUERY_KEY);
    const nextSearch = nextParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );

  };

  const handlePrev = () => {
    setActiveSlide((prev) => (prev === 0 ? prev : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const handleJoinClick = () => {
    handleClose();
    navigate(PagesRoutes.Registration);
  };

  const slide = slides[activeSlide];

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-label="Fanaticka onboarding"
    >
      <PopupFrame>
        <Card>
          <ImageWrapper>
            <TapZone type="button" aria-label="Previous slide" direction="left" onClick={handlePrev} />
            <SlideImage src={slide.image} alt={`Fanaticka stage ${activeSlide + 1}`} />
            <TapZone
              type="button"
              aria-label={slide.showCta ? 'Final slide' : 'Next slide'}
              direction="right"
              onClick={() => {
                if (!slide.showCta) {
                  handleNext();
                }
              }}
            />
            {slide.showCta && (
              <CloseButton type="button" aria-label="Close popup" onClick={handleClose}>
                x
              </CloseButton>
            )}
            {slide.showCta && (
              <JoinLink
                href="https://wa.me/37128781792"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClose}
              >
                CHAT WITH US
              </JoinLink>
            )}
          </ImageWrapper>
          <Dots>
            {slides.map((item, index) => (
              <Dot
                key={item.id}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                $isActive={index === activeSlide}
                onClick={() => setActiveSlide(index)}
              />
            ))}
          </Dots>
        </Card>
      </PopupFrame>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  @media (max-width: 640px) {
    padding: 16px 8px;
  }
`;

const PopupFrame = styled.div`
  width: min(100%, 830px);
  display: flex;
  justify-content: center;
`;

const Card = styled.div`
  width: min(100%, 830px);
  justify-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SlideImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  max-height: calc(100vh - 120px);
  max-height: calc(100dvh - 120px);
  object-fit: contain;
  border-radius: 28px;

  @media (max-width: 640px) {
    max-height: calc(100vh - 110px);
    max-height: calc(100dvh - 110px);
    border-radius: 20px;
  }
`;

const TapZone = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  border: none;
  background: transparent;
  cursor: pointer;
  width: 22%;
  ${(props) => (props.direction === 'left' ? 'left: 0;' : 'right: 0;')}

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
  }

  @media (max-width: 640px) {
    width: 24%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 152px;
  z-index: 2;
  border: none;
  background: transparent;
  color: rgba(164, 164, 164, 0.95);
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 4px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);

  &:hover {
    color: rgba(205, 205, 205, 1);
  }

  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
  }

  @media (max-width: 640px) {
    top: 10px;
    right: 36px;
    font-size: 22px;
  }
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const Dot = styled.button<{ $isActive: boolean }>`
  width: ${(props) => (props.$isActive ? '28px' : '10px')};
  height: 10px;
  border-radius: 999px;
  border: none;
  background: ${(props) => (props.$isActive ? AppTheme.colors.accent : 'rgba(255, 255, 255, 0.28)')};
  cursor: pointer;
  transition: width 0.2s ease, background 0.2s ease;
  padding: 0;

  @media (max-width: 640px) {
    width: ${(props) => (props.$isActive ? '22px' : '8px')};
    height: 8px;
  }
`;

const JoinLink = styled.a`
  position: absolute;
  left: 50%;
  bottom: 6.9%;
  transform: translateX(-50%);
  width: 56%;
  min-height: 7.2%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border: none;
  border-radius: 999px;
  background: ${AppTheme.colors.accent};
  color: #ffffff;
  font-family: ${AppTheme.fonts.blackCapsFont};
  font-size: clamp(12px, 1.7vw, 21px);
  letter-spacing: 0.03em;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.32);
  animation: ctaPulse 1.5s ease-in-out infinite;

  @keyframes ctaPulse {
    0% {
      transform: translateX(-50%) scale(1);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.32);
    }
    50% {
      transform: translateX(-50%) scale(1.04);
      box-shadow: 0 16px 34px rgba(0, 0, 0, 0.36), 0 0 0 10px rgba(255, 255, 255, 0.08);
    }
    100% {
      transform: translateX(-50%) scale(1);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.32);
    }
  }

  &:hover {
    filter: brightness(1.03);
    animation-play-state: paused;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.24), 0 12px 28px rgba(0, 0, 0, 0.32);
  }

  @media (max-width: 640px) {
    width: 58%;
    bottom: 7.1%;
    font-size: clamp(10px, 2.3vw, 16px);
  }
`;
